import { AxiosInstance } from 'axios'
import { MicrosoftAttachment, MicrosoftInlineAttachment } from '@libs/azure-graph'
import { base64ToArrayBuffer } from '../models/file'
import { graphAxios } from './azureServices'

const defaultOptions = {
  client: graphAxios
}

interface AzureServicesOptions {
  client: AxiosInstance
}

export interface EmailAttachmentWithContent {
  id: string
  buffer: ArrayBuffer
  contentType: string
  name: string
  isInline: boolean
}

export class AzureGraphEmailService {
  private cli: AxiosInstance

  // TODO: Move all the methods below to this class
  // TODO: Move auth logic + interceptors from apps/web-client/src/layouts/DashboardLayout/index.tsx to here
  constructor(opts: AzureServicesOptions) {
    const config = { ...defaultOptions, ...opts }

    this.cli = config.client
  }

  private queryParams(params: Record<string, string | number | boolean>) {
    return Object.entries(params)
      .map(([k, v]) => `${k}=${v}`)
      .join('&')
  }

  public async findEmailByInternetMessageId(
    internetMessageId: string,
    mailboxes: string[]
  ): Promise<{ mailbox: string; email: MicrosoftEmail } | null> {
    const response = await this.cli.post<{
      responses: { id: string; status: number; headers: Record<string, string>; body: { value: MicrosoftEmail[] } }[]
    }>(`$batch`, {
      requests: mailboxes.map((mailboxAddress, idx) => ({
        id: mailboxAddress,
        method: 'GET',
        url: `/users/${mailboxAddress}/messages?$filter=internetMessageId eq '${internetMessageId}'`
      }))
    })

    /**
     * Note: Batch API does not return the responses in the order in which were requested, causing issues
     */

    for (const rspIdx in response.data.responses) {
      const rsp = response.data.responses[rspIdx]
      if (rsp.status == 200 && rsp.body.value.length > 0) return { mailbox: rsp.id, email: rsp.body.value[0] }
    }

    return null
  }

  /**
   * Attempts to find the given emailID in the specified mailboxes
   * @param emailId
   * @param mailboxes
   */
  public async findEmailInSharedInboxes(emailId: string, mailboxes: string[]): Promise<{ mailbox: string; email: MicrosoftEmail } | null> {
    const response = await this.cli.post<{
      responses: { id: string; status: number; headers: Record<string, string>; body: MicrosoftEmail }[]
    }>(`$batch`, {
      requests: mailboxes.map((mailboxAddress, idx) => ({
        id: idx,
        method: 'GET',
        url: `/users/${mailboxAddress}/messages/${emailId}`
      }))
    })

    console.log('find in mailboxes', mailboxes, response)

    for (const rspIdx in response.data.responses) {
      const rsp = response.data.responses[rspIdx]
      if (rsp.status === 200) return { mailbox: mailboxes[rspIdx], email: rsp.body }
    }

    return null
  }

  /**
   * Returns a list of attachments without the binary contents
   * To get the binary contents, use retrieveEmailAttachmentContent
   * @param mailboxEmailAddress
   * @param emailId
   */
  public async retrieveEmail(mailboxEmailAddress: string, emailId: string): Promise<MicrosoftEmail> {
    const response = await this.cli.get<MicrosoftEmail>(`users/${mailboxEmailAddress}/messages/${emailId}`)
    return response.data
  }

  /**
   * Returns a list of attachments without the binary contents
   * To get the binary contents, use retrieveEmailAttachmentContent
   * @param mailboxEmailAddress
   * @param emailId
   */
  public async listEmailAttachments(mailboxEmailAddress: string, emailId: string): Promise<MicrosoftAttachment[]> {
    const urlArgs = this.queryParams({ $select: 'id,size,name,contentType,isInline' })

    const response = await this.cli.get<{ value: MicrosoftAttachment[] }>(`users/${mailboxEmailAddress}/messages/${emailId}/attachments?${urlArgs}`)

    return response.data.value
  }

  /**
   * Retrieve an email attachment with the binary contents
   */
  public async retrieveEmailAttachmentContent(
    {
      mailboxEmailAddress,
      emailId,
      attachmentId
    }: {
      mailboxEmailAddress: string
      emailId: string
      attachmentId: string
    },
    onProgress?: (progress: number) => void
  ): Promise<ArrayBuffer> {
    onProgress?.(0)

    const response = await this.cli.get<MicrosoftInlineAttachment>(`users/${mailboxEmailAddress}/messages/${emailId}/attachments/${attachmentId}`, {
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          onProgress!((progressEvent.loaded / progressEvent.total) * 100)
        }
      }
    })

    // in case it was never called
    onProgress?.(100)

    return base64ToArrayBuffer(response.data.contentBytes)
  }

  /**
   * Lists all the binrary contents fo all the attachments of an email
   * @param emailId
   * @param attachmentId
   * @param mailboxEmailAddress
   */
  public async listEmailAttachmentsContent({
    mailboxEmailAddress,
    emailId
  }: {
    mailboxEmailAddress: string
    emailId: string
  }): Promise<EmailAttachmentWithContent[]> {
    const urlArgs = this.queryParams({ $select: 'id,contentType,contentBytes,name' })

    const response = await this.cli.get<{ value: MicrosoftInlineAttachment[] }>(
      `users/${mailboxEmailAddress}/messages/${emailId}/attachments?${urlArgs}`
    )

    return response.data.value.map(
      (att) => ({
        id: att.id,
        buffer: base64ToArrayBuffer(att.contentBytes),
        contentType: att.contentType,
        name: att.name,
        isInline: att.isInline
      }),
      {}
    )
  }

  /**
   * Batch lists the binary contents of the specified attachment IDs
   *  Uses the $batch API to reduce rate limiting issues
   * @param emailId
   * @param attachmentId
   * @param mailboxEmailAddress
   */
  public async listEmailBodyAttachments({
    mailboxEmailAddress,
    emailId,
    attachmentIds
  }: {
    mailboxEmailAddress: string
    emailId: string
    attachmentIds: string[]
  }): Promise<EmailAttachmentWithContent[]> {
    // const urlArgs = this.queryParams({ $select: 'id,contentType,contentBytes,name' })

    const response = await this.cli.post<{
      responses: { id: string; status: number; headers: Record<string, string>; body: MicrosoftInlineAttachment }[]
    }>(`$batch`, {
      requests: attachmentIds.map((id, idx) => ({
        id: idx,
        method: 'GET',
        url: `/users/${mailboxEmailAddress}/messages/${emailId}/attachments/${id}` // ?${urlArgs}
      }))
    })

    console.log('batch req', response.data)

    return response.data.responses.map(({ body: att }) => ({
      id: att.id,
      buffer: base64ToArrayBuffer(att.contentBytes),
      contentType: att.contentType,
      name: att.name,
      isInline: att.isInline
    }))
  }
}

const instance = new AzureGraphEmailService(defaultOptions)
export default instance
